import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";
import SearchIcon from "@mui/icons-material/Search";
import { HubConnectionState } from "@microsoft/signalr";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSnackbar } from "../../contexts/snackbar-context";
import { useMiniPackGenerationSignalR } from "../../hooks/useMiniPackGenerationSignalR";
import { MiniPackGenerationJobDto, useMiniPackGenerationService } from "../../services/miniPackGenerationService";
import {
  filterMiniPackJobsBySearch,
  jobMatchesScopeLabelFilter,
} from "../../utils/miniPackExportJobUtils";

const statusLabel = (status: number) => {
  if (status === 0) {
    return "Queued";
  }

  if (status === 1) {
    return "Processing";
  }

  if (status === 2) {
    return "Completed";
  }

  return "Failed";
};

const statusColor = (status: number): "default" | "success" | "error" | "warning" | "info" => {
  if (status === 2) {
    return "success";
  }

  if (status === 3) {
    return "error";
  }

  if (status === 1) {
    return "warning";
  }

  return "info";
};

const formatDateTime = (value?: string) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

const formatBytes = (value?: number) => {
  if (!Number.isFinite(value) || !value || value <= 0) {
    return "-";
  }

  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = value;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size.toFixed(unitIndex === 0 ? 0 : 2)} ${units[unitIndex]}`;
};

const queueStaleThresholdMs = 10 * 60 * 1000;
const DEFAULT_ROWS_PER_PAGE = 10;
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];
const MAX_JOBS_FETCH = 200;

const isStaleQueued = (job: MiniPackGenerationJobDto) => {
  if (job.status !== 0 || job.startedAtUtc) {
    return false;
  }

  const created = new Date(job.createdAtUtc).getTime();
  if (Number.isNaN(created)) {
    return false;
  }

  return Date.now() - created >= queueStaleThresholdMs;
};

const getJobReason = (job: MiniPackGenerationJobDto) => {
  if (job.status === 3) {
    return job.errorMessage?.trim() || "Mini-pack generation failed.";
  }

  if (job.status === 0) {
    return "Queued: waiting for queue worker pickup. If this stays queued for long, use Retry.";
  }

  if (job.status === 1) {
    return "Mini-pack generation is currently being processed.";
  }

  return "Mini-pack generation completed successfully.";
};

const areJobsEquivalent = (left: MiniPackGenerationJobDto[], right: MiniPackGenerationJobDto[]) => {
  if (left.length !== right.length) {
    return false;
  }

  for (let index = 0; index < left.length; index += 1) {
    const lhs = left[index];
    const rhs = right[index];
    if (
      lhs.jobId !== rhs.jobId
      || lhs.status !== rhs.status
      || lhs.progressPercent !== rhs.progressPercent
      || lhs.createdAtUtc !== rhs.createdAtUtc
      || lhs.completedAtUtc !== rhs.completedAtUtc
      || lhs.fileSizeBytes !== rhs.fileSizeBytes
      || lhs.errorMessage !== rhs.errorMessage
    ) {
      return false;
    }
  }

  return true;
};

const MiniPackGeneratorPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showError, showInfo, showSuccess } = useSnackbar();
  const { listJobs, retryJob, downloadJob } = useMiniPackGenerationService();
  const listJobsRef = useRef(listJobs);
  const showErrorRef = useRef(showError);

  const scope = searchParams.get("scope") || "assembly";
  const scopeLabel = searchParams.get("label") || "All";

  const [jobs, setJobs] = useState<MiniPackGenerationJobDto[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    listJobsRef.current = listJobs;
  }, [listJobs]);

  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  const loadJobs = useCallback(async (withSpinner = false) => {
    if (withSpinner) {
      setLoadingJobs(true);
    }

    try {
      const items = await listJobsRef.current(MAX_JOBS_FETCH);
      const normalized = items || [];
      setJobs((previous) => (areJobsEquivalent(previous, normalized) ? previous : normalized));
    } catch (error) {
      showErrorRef.current((error as Error).message || "Failed to load generation jobs.");
    } finally {
      if (withSpinner) {
        setLoadingJobs(false);
      }
    }
  }, []);

  const upsertJob = useCallback((incoming: MiniPackGenerationJobDto) => {
    setJobs((previous) => {
      const index = previous.findIndex((x) => x.jobId === incoming.jobId);
      const next = [...previous];

      if (index >= 0) {
        next[index] = incoming;
      } else {
        next.unshift(incoming);
      }

      next.sort((a, b) => {
        const aTicks = new Date(a.createdAtUtc || 0).getTime();
        const bTicks = new Date(b.createdAtUtc || 0).getTime();
        return bTicks - aTicks;
      });

      return next;
    });
  }, []);

  const handleConnectionError = useCallback((message: string) => {
    showError(message);
  }, [showError]);

  const { isConnected, connectionState } = useMiniPackGenerationSignalR({
    onJobUpdated: upsertJob,
    onConnectionError: handleConnectionError,
  });

  useEffect(() => {
    void loadJobs(true);
  }, [loadJobs]);

  const hasActiveJobs = useMemo(
    () => jobs.some((job) => job.status === 0 || job.status === 1),
    [jobs]
  );

  useEffect(() => {
    // Safety net polling:
    // - SignalR is primary.
    // - While connected, keep only a slow guard poll.
    // - While disconnected/reconnecting, use faster fallback polling.
    const intervalMs = isConnected
      ? (hasActiveJobs ? 30000 : 0)
      : (hasActiveJobs ? 15000 : 45000);

    if (!intervalMs) {
      return;
    }

    const fallbackPollId = window.setInterval(() => {
      void loadJobs(false);
    }, intervalMs);

    return () => {
      window.clearInterval(fallbackPollId);
    };
  }, [isConnected, hasActiveJobs, loadJobs]);

  useEffect(() => {
    // Pull once on reconnect to recover any updates missed during reconnect windows.
    if (isConnected) {
      void loadJobs(false);
    }
  }, [isConnected, loadJobs]);

  const filteredJobs = useMemo(() => {
    const scopedJobs = scopeLabel === "All"
      ? jobs
      : jobs.filter((job) => jobMatchesScopeLabelFilter(job, scopeLabel));

    return filterMiniPackJobsBySearch(scopedJobs, searchQuery);
  }, [jobs, scopeLabel, searchQuery]);

  const pagedJobs = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredJobs.slice(start, start + rowsPerPage);
  }, [filteredJobs, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, scopeLabel, rowsPerPage]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredJobs.length / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredJobs.length, page, rowsPerPage]);

  const handlePageChange = (_event: unknown, nextPage: number) => {
    setPage(nextPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ width: "100%", mt: 2, p: { xs: 1, md: 2 } }}>
      <Stack spacing={2}>
        <Paper sx={{ backgroundColor: "#111", border: "1px solid #333", color: "#fff", p: { xs: 1.5, md: 2 } }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={1.5}>
            <Box>
              <Typography variant="h6" sx={{ color: "hsl(19 101% 51% / 1)", fontWeight: 700 }}>
                Mini Pack Export Jobs
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)", mt: 0.5 }}>
                Scope: {scopeLabel}
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
                This page is job-monitor only. Configure sections and attachments on the assembly page.
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)", display: "block" }}>
                Realtime: {isConnected ? "Connected" : connectionState === HubConnectionState.Reconnecting ? "Reconnecting" : "Disconnected (fallback refresh active)"}
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                void loadJobs(true);
              }}
              sx={{
                color: "hsl(19 101% 51% / 1)",
                borderColor: "hsl(19 101% 51% / 1)",
                textTransform: "none",
              }}
            >
              Refresh
            </Button>
          </Stack>
        </Paper>

        <Paper sx={{ backgroundColor: "#111", border: "1px solid #333", color: "#fff", p: { xs: 1.5, md: 2 } }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            spacing={1.5}
            sx={{ mb: 1.5 }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Generation Jobs ({scope})
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                placeholder="Search jobs..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "rgba(255,255,255,0.7)" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: { xs: "100%", md: 280 },
                  "& .MuiInputBase-root": {
                    color: "#fff",
                    backgroundColor: "#1a1a1a",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#444",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "rgba(255,255,255,0.55)",
                    opacity: 1,
                  },
                }}
              />
              {loadingJobs && <CircularProgress size={18} sx={{ color: "hsl(19 101% 51% / 1)" }} />}
            </Stack>
          </Stack>

          <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: "#fff", borderColor: "#333" }}>Name</TableCell>
                <TableCell sx={{ color: "#fff", borderColor: "#333" }}>Type</TableCell>
                <TableCell sx={{ color: "#fff", borderColor: "#333" }}>Status</TableCell>
                <TableCell sx={{ color: "#fff", borderColor: "#333" }}>Progress</TableCell>
                <TableCell sx={{ color: "#fff", borderColor: "#333" }}>Size</TableCell>
                <TableCell sx={{ color: "#fff", borderColor: "#333" }}>Created</TableCell>
                <TableCell sx={{ color: "#fff", borderColor: "#333" }}>Completed</TableCell>
                <TableCell sx={{ color: "#fff", borderColor: "#333" }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredJobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ color: "rgba(255,255,255,0.7)", borderColor: "#333", textAlign: "center", py: 3 }}>
                    {jobs.length === 0
                      ? "No mini-pack generation jobs yet."
                      : "No jobs match your search."}
                  </TableCell>
                </TableRow>
              ) : (
                pagedJobs.map((job) => (
                  <TableRow key={job.jobId}>
                    <TableCell sx={{ color: "#fff", borderColor: "#333", maxWidth: 260 }}>
                      <Typography variant="body2" sx={{ color: "#fff" }} noWrap title={job.scopeLabel || "-"}>
                        {job.scopeLabel || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: "#fff", borderColor: "#333" }}>{job.jobType || "-"}</TableCell>
                    <TableCell sx={{ borderColor: "#333" }}>
                      <Tooltip title={job.errorMessage || ""}>
                        <Chip size="small" label={statusLabel(job.status)} color={statusColor(job.status)} />
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ color: "#fff", borderColor: "#333" }}>{job.progressPercent}%</TableCell>
                    <TableCell sx={{ color: "#fff", borderColor: "#333" }}>{formatBytes(job.fileSizeBytes)}</TableCell>
                    <TableCell sx={{ color: "#fff", borderColor: "#333" }}>{formatDateTime(job.createdAtUtc)}</TableCell>
                    <TableCell sx={{ color: "#fff", borderColor: "#333" }}>{formatDateTime(job.completedAtUtc)}</TableCell>
                    <TableCell sx={{ borderColor: "#333" }}>
                      {(job.status === 3 || (job.status === 0 && isStaleQueued(job))) && (
                        <Button
                          size="small"
                          startIcon={<ReplayIcon />}
                          variant="outlined"
                          onClick={async () => {
                            try {
                              const updated = await retryJob(job.jobId);
                              upsertJob(updated);
                              showSuccess("Mini-pack generation job moved back to queue.");
                            } catch (error) {
                              showError((error as Error).message || "Unable to retry mini-pack generation job.");
                            }
                          }}
                          sx={{
                            color: "hsl(19 101% 51% / 1)",
                            borderColor: "hsl(19 101% 51% / 1)",
                            textTransform: "none",
                            mr: 1,
                          }}
                        >
                          Retry
                        </Button>
                      )}

                      {job.status === 2 && (
                        <Button
                          size="small"
                          startIcon={<DownloadIcon />}
                          variant="outlined"
                          onClick={async () => {
                            try {
                              showInfo("Starting browser download. Large files may take a moment to appear in your browser downloads.");
                              const result = await downloadJob(job);
                              if (result) {
                                showSuccess(`Download started in browser for ${job.fileName || "mini-pack.pdf"}.`);
                              } else {
                                showError("Failed to start mini-pack download.");
                              }
                            } catch (error) {
                              showError((error as Error).message || "Failed to download mini-pack.");
                            }
                          }}
                          sx={{
                            color: "hsl(19 101% 51% / 1)",
                            borderColor: "hsl(19 101% 51% / 1)",
                            textTransform: "none",
                            mr: 1,
                          }}
                        >
                          Download
                        </Button>
                      )}

                      {(job.status === 0 || job.status === 3) && (
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => showInfo(getJobReason(job))}
                          sx={{ color: "#fff", textTransform: "none" }}
                        >
                          View reason
                        </Button>
                      )}

                      {job.status === 1 && (
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>
                          In processing
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {filteredJobs.length > DEFAULT_ROWS_PER_PAGE && (
            <TablePagination
              component="div"
              count={filteredJobs.length}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              sx={{
                color: "#fff",
                borderTop: "1px solid #333",
                "& .MuiTablePagination-selectIcon, & .MuiTablePagination-actions, & .MuiTablePagination-displayedRows": {
                  color: "#fff",
                },
              }}
            />
          )}
          </TableContainer>
        </Paper>

        <Button
          variant="text"
          sx={{ alignSelf: "flex-start", color: "#bbb", textTransform: "none" }}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      </Stack>
    </Box>
  );
};

export default MiniPackGeneratorPage;
